import { useState, useCallback, useMemo } from 'react';

import {
  useGetSingleLedger,
  SingleLedgerFilter,
  SingleLedgerApiResponse,
  LedgerTransaction,
} from '../../api/accounts';
import { useAppContext } from '../../contexts/Storage';

interface UseSingleLedgerOptions {
  entityId?: number;
  recordId?: number;
}

export interface LedgerTransactionRow {
  id: number;
  date: string;
  action: string;
  object: string;
  indirectObject: string | null;
  media: string;
  quantity: number;
  amount: number;
}

export interface MappedSingleLedgerData {
  debitSide: {
    items: LedgerTransactionRow[];
    total: number;
    openingBalance: number; // Balance Brought Down (B/B)
    closingBalance: number; // Balance Carried Down (B/C)
    hasBalanceCarriedDown: boolean; // Whether this side has B/C
  };
  creditSide: {
    items: LedgerTransactionRow[];
    total: number;
    openingBalance: number; // Balance Brought Down (B/B)
    closingBalance: number; // Balance Carried Down (B/C)
    hasBalanceCarriedDown: boolean; // Whether this side has B/C
  };
  balanceAmount: number;
  balanceQuantity: number;
  balanceType: string;
}

const getDefaultDateRange = () => {
  const today = new Date();
  const twoYearsAgo = new Date();
  twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    start_date: `${formatDate(twoYearsAgo)} 00:00:00`,
    end_date: `${formatDate(today)} 23:59:59`,
  };
};

const mapTransaction = (tx: LedgerTransaction): LedgerTransactionRow => ({
  id: tx.id,
  date: tx.transaction_date,
  action: tx.action?.transaction_entity_name ?? '',
  object: tx.object?.transaction_entity_name ?? '',
  indirectObject: tx.indirect_object?.transaction_entity_name ?? null,
  media: tx.media?.transaction_entity_name ?? '',
  quantity: tx.transaction_quantity,
  amount: tx.transaction_total_amount,
});

const useSingleLedger = (options: UseSingleLedgerOptions = {}) => {
  const { entityId, recordId: initialRecordId } = options;
  const { user, selectedRecord } = useAppContext();

  const recordId = initialRecordId ?? selectedRecord?.id;
  const userId = user?.id;

  const defaultDates = getDefaultDateRange();

  const [filters, setFilters] = useState<Partial<SingleLedgerFilter>>({
    start_date: defaultDates.start_date,
    end_date: defaultDates.end_date,
  });

  const hasRequiredParams = !!entityId && !!recordId && !!userId && !!filters.start_date && !!filters.end_date;
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useGetSingleLedger({
    filters: {
      ...filters,
      entity_id: entityId,
      record_id: recordId,
      user_id: userId,
    },
    enabled: hasRequiredParams,
  });

  const rawData = useMemo(() => data?.data, [data?.data]);

    const mappedData = useMemo((): MappedSingleLedgerData | null => {
    if (!rawData) return null;

    const response = rawData as SingleLedgerApiResponse;

    const debitItems = (response.debit_transactions ?? []).map(mapTransaction);
    const creditItems = (response.credit_transactions ?? []).map(mapTransaction);

    const debitTotal = debitItems.reduce((sum, item) => sum + item.amount, 0);
    const creditTotal = creditItems.reduce((sum, item) => sum + item.amount, 0);

    // For traditional ledger format:
    // - The side with lower total gets a Balance Carried Down (Bal c/d)
    // - This makes both sides equal
    // - The Balance Brought Down (Bal b/d) appears on opposite side (for next period)
    
    const balanceDifference = Math.abs(debitTotal - creditTotal);
    const debitHigher = debitTotal > creditTotal;
    
    // Determine which side needs balance carried down
    const debitHasBalanceCarriedDown = !debitHigher && balanceDifference > 0;
    const creditHasBalanceCarriedDown = debitHigher && balanceDifference > 0;
    
    // Closing balance (Bal c/d) is the difference needed to make sides equal
    const debitClosingBalance = debitHasBalanceCarriedDown ? balanceDifference : 0;
    const creditClosingBalance = creditHasBalanceCarriedDown ? balanceDifference : 0;
    
    // Opening balance (Bal b/d) - for now, assume zero
    // In a real implementation, this would come from previous period's closing balance
    const debitOpeningBalance = creditHasBalanceCarriedDown ? balanceDifference : 0;
    const creditOpeningBalance = debitHasBalanceCarriedDown ? balanceDifference : 0;

    return {
      debitSide: {
        items: debitItems,
        total: debitTotal,
        openingBalance: debitOpeningBalance,
        closingBalance: debitClosingBalance,
        hasBalanceCarriedDown: debitHasBalanceCarriedDown,
      },
      creditSide: {
        items: creditItems,
        total: creditTotal,
        openingBalance: creditOpeningBalance,
        closingBalance: creditClosingBalance,
        hasBalanceCarriedDown: creditHasBalanceCarriedDown,
      },
      balanceAmount: response.balance_amount ?? 0,
      balanceQuantity: response.balance_quantity ?? 0,
      balanceType: response.balance_type ?? '',
    };
  }, [rawData]);

  const updateFilter = useCallback((updates: Partial<SingleLedgerFilter>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleDateFilter = useCallback((startDate?: string, endDate?: string) => {
    const defaults = getDefaultDateRange();
    updateFilter({
      start_date: startDate ? `${startDate} 00:00:00` : defaults.start_date,
      end_date: endDate ? `${endDate} 23:59:59` : defaults.end_date,
    });
  }, [updateFilter]);

  const resetFilters = useCallback(() => {
    const defaultDates = getDefaultDateRange();
    setFilters({
      start_date: defaultDates.start_date,
      end_date: defaultDates.end_date,
    });
  }, []);

  return {
    rawData,
    mappedData,
    isLoading,
    hasRequiredParams,
    error,
    filters,
    handleDateFilter,
    resetFilters,
    updateFilter,
    refetch,
  };
};

export default useSingleLedger;
